import React, { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import NavBar from "../components/layout/NavBar";
import {
  StepKinds,
  defaultStep,
  FlowStore,
} from "../components/flow/FlowTypes";
import "../components/common/common.css";

const TILE = 128;

const paletteKinds = [
  { kind: StepKinds.input, label: "Input", icon: "🔤" },
  { kind: StepKinds.list, label: "List", icon: "📋" },
  { kind: StepKinds.dateRange, label: "Date", icon: "📅" },
];

function makeStep(kind) {
  return defaultStep(kind);
}

export default function FlowDesignerIsometric() {
  const { t } = useTranslation();
  const [name, setName] = useState("Isometric Flow");
  const [nodes, setNodes] = useState([
    {
      id: 1,
      kind: StepKinds.input,
      data: makeStep(StepKinds.input),
      pos: { x: 0, y: 0 },
      offset: { dx: 0, dy: 0 },
    },
  ]);
  const [selectedKind, setSelectedKind] = useState(StepKinds.input);
  const [editingId, setEditingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineLabel, setInlineLabel] = useState("");
  const [zoom, setZoom] = useState(0.9);
  const [pan, setPan] = useState({ x: 220, y: 220 });
  const [spaceDown, setSpaceDown] = useState(false);
  const [edges, setEdges] = useState([]);
  const dragRef = useRef({
    mode: null,
    nodeId: null,
    startX: 0,
    startY: 0,
    startPan: { x: 0, y: 0 },
    startOffset: { dx: 0, dy: 0 },
  });
  const planeRef = useRef(null);
  const hoverNodeIdRef = useRef(null);
  // nodeRefs and selectionRef removed (unused)

  const steps = useMemo(
    () =>
      nodes.map((n) => {
        const s = n.data;
        if (n.kind === StepKinds.input)
          return {
            kind: s.kind,
            label: s.label,
            placeholder: s.placeholder,
            key: s.key,
          };
        if (n.kind === StepKinds.list)
          return { kind: s.kind, label: s.label, source: s.source, key: s.key };
        if (n.kind === StepKinds.dateRange)
          return {
            kind: s.kind,
            label: s.label,
            fromKey: s.fromKey,
            toKey: s.toKey,
          };
        return s;
      }),
    [nodes]
  );

  const json = useMemo(
    () => JSON.stringify({ name, steps, edges }, null, 2),
    [name, steps, edges]
  );

  // keyboard: Space to pan, Delete to remove
  useEffect(() => {
    const down = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setSpaceDown(true);
      }
      if (
        (e.key === "Delete" || e.key === "Backspace") &&
        inlineEditId == null
      ) {
        setNodes((ns) => ns.filter((n) => !selectedIds.includes(n.id)));
        setEdges((es) =>
          es.filter(
            (ed) =>
              !selectedIds.includes(ed.fromId) && !selectedIds.includes(ed.toId)
          )
        );
        if (selectedIds.includes(editingId)) setEditingId(null);
        setSelectedIds([]);
      }
    };
    const up = (e) => {
      if (e.code === "Space") setSpaceDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [selectedIds, inlineEditId, editingId]);

  const addNode = () => {
    setNodes((ns) => {
      const id = (ns[ns.length - 1]?.id || 0) + 1;
      if (ns.length === 0)
        return [
          {
            id,
            kind: selectedKind,
            data: makeStep(selectedKind),
            pos: { x: 0, y: 0 },
            offset: { dx: 0, dy: 0 },
          },
        ];
      const prev = ns[ns.length - 1];
      // Move 2 units on X to avoid overlap (128px left, 64px top per step at TILE=128)
      const pos = { x: prev.pos.x + 2, y: prev.pos.y };
      return [
        ...ns,
        {
          id,
          kind: selectedKind,
          data: makeStep(selectedKind),
          pos,
          offset: { dx: 0, dy: 0 },
        },
      ];
    });
  };

  const updateNode = (id, patch) =>
    setNodes((ns) => ns.map((n) => (n.id === id ? { ...n, ...patch } : n)));

  const save = () => {
    FlowStore.upsert({ name, steps });
    alert("Flow saved");
  };

  const startInlineRename = (node) => {
    setInlineEditId(node.id);
    setInlineLabel(node.data.label || "");
    setEditingId(node.id);
  };
  const commitInlineRename = () => {
    if (inlineEditId == null) return;
    setNodes((ns) =>
      ns.map((n) =>
        n.id === inlineEditId
          ? { ...n, data: { ...n.data, label: inlineLabel } }
          : n
      )
    );
    setInlineEditId(null);
  };

  // pan handlers
  const onBackgroundMouseDown = (e) => {
    if (!spaceDown) return; // only pan when space is held
    dragRef.current = {
      mode: "pan",
      nodeId: null,
      startX: e.clientX,
      startY: e.clientY,
      startPan: { ...pan },
    };
    window.addEventListener("mousemove", onBackgroundMouseMove);
    window.addEventListener("mouseup", onBackgroundMouseUp);
  };
  const onBackgroundMouseMove = (e) => {
    if (dragRef.current.mode !== "pan") return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPan({
      x: dragRef.current.startPan.x + dx,
      y: dragRef.current.startPan.y + dy,
    });
  };
  const onBackgroundMouseUp = () => {
    dragRef.current = { mode: null };
    window.removeEventListener("mousemove", onBackgroundMouseMove);
    window.removeEventListener("mouseup", onBackgroundMouseUp);
  };
  const onTileMouseDown = (e, node) => {
    e.stopPropagation();
    if (e.shiftKey) {
      setSelectedIds((ids) =>
        ids.includes(node.id)
          ? ids.filter((id) => id !== node.id)
          : [...ids, node.id]
      );
    } else if (!selectedIds.includes(node.id)) {
      setSelectedIds([node.id]);
      setEditingId(node.id);
    }
    dragRef.current = {
      mode: "tile",
      nodeId: node.id,
      startX: e.clientX,
      startY: e.clientY,
      startOffset: { ...(node.offset || { dx: 0, dy: 0 }) },
    };
    window.addEventListener("mousemove", onTileMouseMove);
    window.addEventListener("mouseup", onTileMouseUp);
  };
  const onTileMouseMove = (e) => {
    if (dragRef.current.mode !== "tile") return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const id = dragRef.current.nodeId;
    setNodes((ns) =>
      ns.map((n) => {
        const move =
          selectedIds.length > 0 ? selectedIds.includes(n.id) : n.id === id;
        if (!move) return n;
        const base =
          n.id === id
            ? dragRef.current.startOffset
            : n.offset || { dx: 0, dy: 0 };
        return { ...n, offset: { dx: base.dx + dx, dy: base.dy + dy } };
      })
    );
  };
  const onTileMouseUp = () => {
    dragRef.current = { mode: null };
    document.body.style.cursor = "";
    window.removeEventListener("mousemove", onTileMouseMove);
    window.removeEventListener("mouseup", onTileMouseUp);
    // snap moved nodes to grid by converting pixel offsets to nearest axial coords
    setNodes((ns) =>
      ns.map((n) => {
        if (!selectedIds.includes(n.id)) return n;
        const left = (n.pos.x - n.pos.y) * (TILE / 2) + (n.offset?.dx || 0);
        const top = (n.pos.x + n.pos.y) * (TILE / 4) + (n.offset?.dy || 0);
        const xMinusY = (2 * left) / TILE;
        const xPlusY = (4 * top) / TILE;
        const x = Math.round((xMinusY + xPlusY) / 2);
        const y = Math.round((xPlusY - xMinusY) / 2);
        return { ...n, pos: { x, y }, offset: { dx: 0, dy: 0 } };
      })
    );
  };

  const planeStyle = {
    ...styles.isoPlane,
    transform: `translate(-50%, -30%) translate(${pan.x}px, ${pan.y}px) scale(${zoom}) rotateX(50deg) rotateZ(45deg)`,
    backgroundImage: `repeating-linear-gradient(0deg, rgba(0,0,0,0.06) 0 1px, transparent 1px ${
      TILE / 2
    }px), repeating-linear-gradient(90deg, rgba(0,0,0,0.06) 0 1px, transparent 1px ${
      TILE / 2
    }px)`,
  };

  return (
    <div className="app-root">
      <div className="nav-strip">
        <NavBar />
      </div>
      <header className="top-bar">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div className="ai-chip">{t("flowDesigner.titleIso")}</div>
        </div>
        <div className="search-wrapper" style={{ maxWidth: 560 }}>
          <input
            className="search-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="go-btn" onClick={save}>
            {t("common.save")}
          </button>
        </div>
        <div style={{ flex: 1 }} />
      </header>

      <div
        className="main-layout"
        style={{ gridTemplateColumns: "260px 1fr 360px" }}
      >
        {/* Palette */}
        <nav className="side-panel">
          <h4 style={{ marginTop: 0 }}>{t("flowDesigner.palette")}</h4>
          <div style={{ display: "grid", gap: 8 }}>
            {paletteKinds.map((p) => (
              <button
                key={p.kind}
                className="ai-chip"
                onClick={() => setSelectedKind(p.kind)}
                style={{
                  textAlign: "left",
                  border:
                    selectedKind === p.kind
                      ? "2px solid #222"
                      : "1px solid #ddd",
                }}
                title={t(`flowDesigner.paletteKind.${p.kind}`, {
                  defaultValue: p.label,
                })}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", String(p.kind));
                }}
              >
                {p.icon}{" "}
                {t(`flowDesigner.paletteKind.${p.kind}`, {
                  defaultValue: p.label,
                })}
              </button>
            ))}
          </div>
          <button
            className="go-btn"
            style={{ marginTop: 12 }}
            onClick={addNode}
          >
            {t("flowDesigner.addStep")}
          </button>
          <div style={{ height: 12 }} />
          <a
            className="ai-chip"
            href="#/flow-runner"
            style={{ textDecoration: "none" }}
          >
            ▶ {t("flowDesigner.runFlow")}
          </a>
        </nav>

        {/* Isometric Board */}
        <main
          className="center-panel"
          style={{ position: "relative", overflow: "hidden" }}
          onMouseDown={onBackgroundMouseDown}
        >
          {/* Zoom controls */}
          <div
            style={{
              position: "absolute",
              right: 12,
              top: 12,
              zIndex: 3,
              display: "flex",
              gap: 6,
              alignItems: "center",
              background: "#fff",
              padding: "6px 8px",
              border: "1px solid #ddd",
              borderRadius: 8,
            }}
          >
            <button
              className="icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)));
              }}
              title={t("flowDesigner.zoomOut")}
            >
              −
            </button>
            <span
              style={{
                fontSize: 12,
                minWidth: 40,
                textAlign: "center",
              }}
            >
              {Math.round(zoom * 100)}%
            </span>
            <button
              className="icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)));
              }}
              title={t("flowDesigner.zoomIn")}
            >
              ＋
            </button>
          </div>

          <div
            style={styles.isoWrap}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const kind = e.dataTransfer.getData("text/plain");
              if (!kind) return;
              setNodes((ns) => {
                const id = (ns[ns.length - 1]?.id || 0) + 1;
                const last = ns[ns.length - 1];
                const pos = last
                  ? { x: last.pos.x + 2, y: last.pos.y }
                  : { x: 0, y: 0 };
                return [
                  ...ns,
                  {
                    id,
                    kind,
                    data: makeStep(kind),
                    pos,
                    offset: { dx: 0, dy: 0 },
                  },
                ];
              });
            }}
          >
            <div ref={planeRef} style={planeStyle}>
              {/* connectors behind tiles */}
              {nodes.slice(1).map((n, i) => (
                <IsoConnector key={`c-${n.id}`} from={nodes[i]} to={n} />
              ))}
              {/* tiles */}
              {nodes.map((n, idx) => (
                <IsoNode
                  key={n.id}
                  node={n}
                  index={idx}
                  selected={editingId === n.id || selectedIds.includes(n.id)}
                  inlineEditId={inlineEditId}
                  inlineLabel={inlineLabel}
                  onMouseDown={(e) => onTileMouseDown(e, n)}
                  onClick={(e) => {
                    e.stopPropagation();
                    setInlineEditId(null);
                    if (e.shiftKey) {
                      setSelectedIds((ids) =>
                        ids.includes(n.id)
                          ? ids.filter((id) => id !== n.id)
                          : [...ids, n.id]
                      );
                      setEditingId(n.id);
                    } else {
                      setSelectedIds([n.id]);
                      setEditingId(n.id);
                    }
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    startInlineRename(n);
                  }}
                  onMouseEnter={() => {
                    hoverNodeIdRef.current = n.id;
                  }}
                  onMouseLeave={() => {
                    if (hoverNodeIdRef.current === n.id)
                      hoverNodeIdRef.current = null;
                  }}
                  onInlineLabelChange={setInlineLabel}
                  onInlineLabelCommit={commitInlineRename}
                  onStartLink={(e) => {
                    e.stopPropagation();
                    console.log("start link", n.id);
                  }}
                />
              ))}
            </div>
          </div>
        </main>

        {/* Inspector */}
        <aside className="detail-panel">
          <h3 style={{ marginTop: 0 }}>{t("flowDesigner.inspector")}</h3>
          {editingId == null ? (
            <div style={{ fontSize: 13, color: "#555", lineHeight: 1.5 }}>
              • {t("flowDesigner.help.clickToSelect")}
              <br />• {t("flowDesigner.help.doubleClickRename")}
              <br />• {t("flowDesigner.help.dragToMove")}
              <br />• {t("flowDesigner.help.dragToPan")}
              <br />• {t("flowDesigner.help.useZoom")}
            </div>
          ) : (
            <NodeInspector
              node={nodes.find((n) => n.id === editingId)}
              onChange={(patch) =>
                updateNode(editingId, {
                  data: {
                    ...nodes.find((n) => n.id === editingId).data,
                    ...patch,
                  },
                })
              }
              onKindChange={(kind) =>
                updateNode(editingId, { kind, data: makeStep(kind) })
              }
            />
          )}

          <h3>{t("flowDesigner.previewJson")}</h3>
          <pre style={styles.pre}>{json}</pre>
        </aside>
      </div>
    </div>
  );
}

function NodeInspector({ node, onChange, onKindChange }) {
  const { t } = useTranslation();
  if (!node) return null;
  const { kind, data } = node;
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <label className="label">{t("flowDesigner.type")}</label>
      <select
        className="search-input"
        value={kind}
        onChange={(e) => onKindChange(e.target.value)}
      >
        {Object.values(StepKinds).map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
      </select>

      {kind === StepKinds.input && (
        <>
          <label className="label">{t("flowDesigner.label")}</label>
          <input
            className="search-input"
            value={data.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
          />
          <label className="label">{t("flowDesigner.placeholder")}</label>
          <input
            className="search-input"
            value={data.placeholder || ""}
            onChange={(e) => onChange({ placeholder: e.target.value })}
          />
          <label className="label">{t("flowDesigner.contextKey")}</label>
          <input
            className="search-input"
            value={data.key || ""}
            onChange={(e) => onChange({ key: e.target.value })}
          />
        </>
      )}

      {kind === StepKinds.list && (
        <>
          <label className="label">{t("flowDesigner.label")}</label>
          <input
            className="search-input"
            value={data.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
          />
          <label className="label">{t("flowDesigner.source")}</label>
          <input
            className="search-input"
            value={data.source || ""}
            onChange={(e) => onChange({ source: e.target.value })}
          />
          <label className="label">{t("flowDesigner.contextKey")}</label>
          <input
            className="search-input"
            value={data.key || ""}
            onChange={(e) => onChange({ key: e.target.value })}
          />
        </>
      )}

      {kind === StepKinds.dateRange && (
        <>
          <label className="label">{t("flowDesigner.label")}</label>
          <input
            className="search-input"
            value={data.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
          />
          <label className="label">{t("flowDesigner.fromKey")}</label>
          <input
            className="search-input"
            value={data.fromKey || ""}
            onChange={(e) => onChange({ fromKey: e.target.value })}
          />
          <label className="label">{t("flowDesigner.toKey")}</label>
          <input
            className="search-input"
            value={data.toKey || ""}
            onChange={(e) => onChange({ toKey: e.target.value })}
          />
        </>
      )}
    </div>
  );
}

function IsoNode({
  node,
  index,
  selected,
  inlineEditId,
  inlineLabel,
  onMouseDown,
  onClick,
  onDoubleClick,
  onMouseEnter,
  onMouseLeave,
  onInlineLabelChange,
  onInlineLabelCommit,
  onStartLink,
}) {
  const { kind, data, pos, offset } = node;
  const left = (pos.x - pos.y) * (TILE / 2) + (offset?.dx || 0);
  const top = (pos.x + pos.y) * (TILE / 4) + (offset?.dy || 0);
  const emoji =
    kind === StepKinds.input ? "🔤" : kind === StepKinds.list ? "📋" : "📅";
  const isInline = inlineEditId === node.id;
  return (
    <div
      onMouseDown={onMouseDown}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onDragStart={(e) => e.preventDefault()}
      style={{
        ...styles.isoTile,
        left,
        top,
        borderColor: selected ? "#2563eb" : "#222",
        boxShadow: selected
          ? "0 0 0 3px rgba(37,99,235,0.35), 0 6px 0 rgba(0,0,0,0.2)"
          : "0 6px 0 rgba(0,0,0,0.2)",
        zIndex: 2,
      }}
      title={data.label || kind}
    >
      {/* face content tilted with the tile */}
      <div style={styles.tileInner}>
        <div style={{ fontSize: 22 }}>{emoji}</div>
        {isInline ? (
          <input
            autoFocus
            className="search-input"
            style={{ padding: "6px 8px", fontSize: 14, transform: "none" }}
            value={inlineLabel}
            onChange={(e) => onInlineLabelChange(e.target.value)}
            onBlur={onInlineLabelCommit}
            onKeyDown={(e) => {
              if (e.key === "Enter") onInlineLabelCommit();
            }}
          />
        ) : (
          <div style={styles.tileLabel}>{data.label || kind}</div>
        )}
      </div>
      {/* link handle */}
      <button
        title="Drag to link"
        onMouseDown={onStartLink}
        style={styles.linkHandle}
      />
    </div>
  );
}

function IsoConnector({ from, to, dashed }) {
  const ax =
    (from.pos.x - from.pos.y) * (TILE / 2) + (from.offset?.dx || 0) + TILE / 2;
  const ay =
    (from.pos.x + from.pos.y) * (TILE / 4) + (from.offset?.dy || 0) + TILE / 4;
  const bx =
    (to.pos.x - to.pos.y) * (TILE / 2) + (to.offset?.dx || 0) + TILE / 2;
  const by =
    (to.pos.x + to.pos.y) * (TILE / 4) + (to.offset?.dy || 0) + TILE / 4;
  const length = Math.hypot(bx - ax, by - ay);
  const angle = (Math.atan2(by - ay, bx - ax) * 180) / Math.PI;
  return (
    <div
      style={{
        position: "absolute",
        left: ax,
        top: ay,
        width: length,
        height: 0,
        borderTop: dashed ? "2px dashed #64748b" : "2px solid #94a3b8",
        transform: `rotate(${angle}deg)`,
        transformOrigin: "0 0",
        zIndex: 1,
      }}
    />
  );
}

const styles = {
  isoWrap: {
    position: "relative",
    height: "100%",
    overflow: "hidden",
    background: "#f7f7f9",
  },
  isoPlane: {
    position: "absolute",
    left: "50%",
    top: "30%",
    transformStyle: "preserve-3d",
    width: 2000,
    height: 1200,
    backgroundSize: `${TILE / 2}px ${TILE / 2}px`,
  },
  isoTile: {
    position: "absolute",
    width: TILE,
    height: TILE / 2,
    background: "#fff",
    border: "2px solid #222",
    borderRadius: 12,
    boxShadow: "0 6px 0 rgba(0,0,0,0.2)",
    cursor: "grab",
    userSelect: "none",
    display: "grid",
    placeItems: "center",
    padding: 6,
  },
  tileInner: {
    transform: "rotateX(-50deg) rotateZ(-45deg) translateZ(18px)",
    transformStyle: "preserve-3d",
    display: "grid",
    placeItems: "center",
    gap: 6,
    height: "100%",
    padding: "6px 10px",
  },
  tileLabel: {
    fontWeight: 700,
    fontSize: 15,
    textAlign: "center",
    maxWidth: TILE - 24,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  linkHandle: {
    position: "absolute",
    right: -10,
    top: "50%",
    transform: "translateY(-50%)",
    width: 12,
    height: 12,
    borderRadius: 12,
    background: "#2563eb",
    border: "2px solid #fff",
    boxShadow: "0 0 0 2px rgba(37,99,235,0.3)",
    cursor: "crosshair",
  },
  pre: {
    background: "#111",
    color: "#f1f1f1",
    padding: 12,
    borderRadius: 8,
    overflow: "auto",
  },
};
